<template>
  <div>
    <div v-if="priceloading && prices.length === 0" class="d-flex justify-center align-center py-10">
      <atom-spinner
        :animation-duration="1500"
        :size="100"
        class="mx-auto"
        color="var(--v-primary-base)"
      />
    </div>

    <div v-else-if="!priceloading && prices.length === 0" class="text-center py-10">
      <v-icon size="64" color="grey lighten-1">mdi-package-variant-closed</v-icon>
      <div class="mt-4 text-subtitle-1 grey--text">هیچ تعرفه‌ای برای این نوع یافت نشد.</div>
    </div>

    <div v-else>
      <div key="prices" class="pa-2 pa-sm-4">
        <custom-tabs
          v-model="tabs2"
          :tab-items="prices"
          :window-items="prices"
          fixed-tabs
        >
          <template v-slot:tab="{ tab }">
            <span class="font-weight-bold">{{ tab.name || (persian(tab.time) + ' روزه') }}</span>
          </template>
          
          <template v-slot:item="{ item }">
            <v-card 
              class="mx-auto rounded-xl mt-6 overflow-hidden border position-relative" 
              elevation="0" 
              :style="`border: 2px solid ${getCardStyle(item).color}44 !important;`"
            >
              
              <!-- لوگوی بالا چپ -->
              <v-img
                v-if="socialImageSrc"
                :src="socialImageSrc"
                max-width="32"
                max-height="32"
                contain
                style="position: absolute; top: 16px; left: 16px; opacity: 0.7;"
              />
              <v-icon 
                v-else
                color="#b0bec5" 
                size="32" 
                style="position: absolute; top: 16px; left: 16px;"
              >
                mdi-{{ iconQr }}
              </v-icon>

              <v-card-text class="pa-6 text-center pb-2">
                
                <div class="d-flex flex-column align-center mb-6 mt-2">
                  <v-icon :color="getCardStyle(item).color" size="40" class="mb-2">
                    {{ getCardStyle(item).icon }}
                  </v-icon>
                  <v-chip
                    :color="getCardStyle(item).color"
                    class="font-weight-bold px-5"
                    dark
                    style="font-size: 14px;"
                  >
                    {{ getCardStyle(item).tag }}
                  </v-chip>
                </div>

                <div class="d-flex flex-column align-center justify-center pa-4 rounded-xl mb-4" style="background: #f8f9fa;">
                  
                  <!-- قیمت قبل از تخفیف -->
                  <div v-if="!!item.discount" class="text-decoration-line-through grey--text text--lighten-1 mb-2">
                    {{ persian(formatPrice(item.price).num) }} {{ formatPrice(item.price).text }}
                  </div>
                  
                  <!-- قیمت نهایی -->
                  <div class="d-flex align-baseline justify-center" style="gap: 6px;">
                    <span 
                      class="font-weight-black" 
                      :style="`font-size: 2rem; line-height: 1; color: ${getCardStyle(item).color};`"
                    >
                      {{ persian(formatPrice(!!item.discount ? computedDiscount(item) : item.price).num) }}
                    </span>
                    <span class="text-subtitle-1 grey--text text--darken-2 font-weight-medium">
                      {{ formatPrice(!!item.discount ? computedDiscount(item) : item.price).text }}
                    </span>
                  </div>

                  <!-- درصد تخفیف -->
                  <v-chip
                    v-if="!!item.discount"
                    class="mt-3 font-weight-bold px-4"
                    color="#ec407a"
                    dark
                    small
                  >
                    {{ persian(item.discount) }}% تخفیف
                  </v-chip>
                </div>

                <!-- تعداد روز -->
                <div class="text-center mt-2 mb-2">
                  <span class="font-weight-black" style="font-size: 3rem; color: #2c3e50; line-height: 1;">{{ persian(item.time) }}</span>
                  <span class="grey--text text-h6 ml-2 font-weight-regular">روز</span>
                </div>
              </v-card-text>

              <v-divider class="mx-4 mt-2"></v-divider>

              <!-- لیست ویژگی‌ها (Features) -->
              <div class="px-6 py-4">
                <v-list dense class="transparent pa-0">
                  <v-list-item 
                    v-for="(feature, idx) in getFeatures(item)" 
                    :key="idx" 
                    class="px-0 mb-1" 
                    style="min-height: 32px;"
                  >
                    <v-list-item-icon class="ml-3 mr-0 my-0 align-self-center">
                      <v-icon small color="success">mdi-check-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="py-1">
                      <v-list-item-title 
                        class="text-right text-subtitle-2 grey--text text--darken-3 font-weight-medium" 
                        style="white-space: normal; line-height: 1.5;"
                      >
                        {{ feature }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>

              <v-card-actions class="pa-4 flex-column">
                <v-btn
                  block
                  :disabled="discountField.show"
                  :loading="priceloading"
                  :color="discountField.show ? 'grey lighten-3' : 'success'"
                  class="rounded-lg mb-2 font-weight-bold text-h6"
                  elevation="0"
                  x-large
                  @click="upgradeQr(item.id)"
                >
                  <v-icon size="18" class="ml-1">mdi-cart-outline</v-icon>
                  {{ $t("i_buy") }}
                </v-btn>
                
                <v-btn
                  block
                  :loading="priceloading"
                  color="blue-grey darken-3"
                  text
                  class="rounded-lg text-subtitle-2"
                  @click="
                    discountField.show = !discountField.show;
                    priceId = item.id;
                  "
                >
                  {{ discountField.show ? 'بستن فرم تخفیف' : $t("add_discount_code") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </template>
        </custom-tabs>
      </div>

      <!-- فرم کد تخفیف -->
      <v-expand-transition>
        <div v-if="discountField.show" key="discount" class="px-2 px-sm-4 pb-4 mt-2">
          <v-card class="rounded-xl border pa-1" elevation="0" style="border: 1px solid #e0e0e0 !important;">
            <v-card-title class="text-subtitle-2 font-weight-bold pb-2 black--text">
              استفاده از کد تخفیف
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="discountField.model"
                clear-icon="mdi-close"
                clearable
                dense
                hide-details="auto"
                label="کد تخفیف"
                outlined
                class="rounded-lg"
                prepend-inner-icon="mdi-percent-box"
              >
                <template v-slot:append-outer>
                  <v-btn
                    :loading="priceloading"
                    class="mt-n1 rounded-lg px-6"
                    color="#26a69a"
                    dark
                    elevation="0"
                    height="40"
                    @click="upgradeQr(priceId)"
                  >
                    ثبت
                  </v-btn>
                </template>
              </v-text-field>
            </v-card-text>
          </v-card>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script>
import { AtomSpinner } from "epic-spinners";

export default {
  name: "Tariffe",
  components: {
    AtomSpinner,
  },
  props: {
    qrtype: { type: String, default: "" },
    id_qr: { type: Number },
  },
  data() {
    return {
      tabs2: null,
      priceId: null,
      priceloading: true,
      prices: [],
      discountField: {
        show: false,
        model: "",
      },
      displayMap: {
        qreitaa: { type: "image", src: "/media/socialnetwork/icon/eitaa.webp", name: "ایتا" },
        qrbale: { type: "image", src: "/media/socialnetwork/icon/bale.webp", name: "بله" },
        qrrobika: { type: "image", src: "/media/socialnetwork/icon/robika.webp", name: "روبیکا" },
        "qr.eitaa": { type: "image", src: "/media/socialnetwork/icon/eitaa.webp", name: "ایتا" },
        "qr.bale": { type: "image", src: "/media/socialnetwork/icon/bale.webp", name: "بله" },
        "qr.robika": { type: "image", src: "/media/socialnetwork/icon/robika.webp", name: "روبیکا" },
        eitaa: { type: "image", src: "/media/socialnetwork/icon/eitaa.webp", name: "ایتا" },
        bale: { type: "image", src: "/media/socialnetwork/icon/bale.webp", name: "بله" },
        robika: { type: "image", src: "/media/socialnetwork/icon/robika.webp", name: "روبیکا" },
      },
    };
  },
  mounted() {
    this.getPrices();
  },
  computed: {
    qrDataObject() {
      const types = (typeof this.getQrTypes === "function" ? this.getQrTypes() : []) || [];
      return (
        types.find(
          (item) => item.value.toLowerCase() === (this.qrtype || "").toLowerCase()
        ) || {}
      );
    },
    socialImageSrc() {
      const key = (this.qrtype || "").toLowerCase().trim();
      const cleanKey = key.replace(/[\._-]/g, "");

      if (this.displayMap[cleanKey]) {
        return this.displayMap[cleanKey].src;
      }
      if (this.displayMap[key]) {
        return this.displayMap[key].src;
      }
      if (this.qrDataObject && this.qrDataObject.type === "image") {
        return this.qrDataObject.src;
      }
      return null;
    },
    iconQr() {
      return this.qrDataObject.icon || "qrcode-scan";
    },
  },
  methods: {
    getFeatures(item) {
      const type = (this.qrtype || "").toLowerCase().trim();

      if (type === "qrlink") {
        let linkFeatures = [
          "اسکن نامحدود و بدون قطعی",
          "امکان تغییر لینک مقصد بدون تغییر کیوآر (داینامیک)",
          "آمار و گزارش تعداد کلیک و اسکن"
        ];
        if (item.time >= 360) {
          linkFeatures.push("پشتیبانی ویژه و اولویت‌دار VIP");
        }
        return linkFeatures;
      }

      // ۲. ویژگی‌های اختصاصی برای clickyarseo (کلیک یار سئو)
      if (type === "clickyarseo") {
        let seoFeatures = [
          "اسکن نامحدود و بدون قطعی",
          "هدایت هوشمند و بهینه‌سازی نرخ کلیک (CTR)",
          "امکان تغییر لینک و مسیردهی بدون تغییر طرح QR",
          "ماندگاری دائمی اطلاعات و متاداده‌ها",
          "ثبت آمار تفکیکی بازدید"
        ];
        if (item.time >= 360) {
          seoFeatures.push("آنالیز پیشرفته سئو و رفتار اسکن کاربران");
          seoFeatures.push("پشتیبانی ویژه و اولویت‌دار VIP");
        }
        return seoFeatures;
      }

      let baseFeatures = [
        "اسکن نامحدود و بدون قطعی",
        "امکان ویرایش محتوا بدون تغییر شکل کیوآر (داینامیک)",
        "شخصی‌سازی کامل تم و قالب گرافیکی",
        "پشتیبانی از خواندن و نوشتن تگ NFC",
        "ماندگاری دائمی اطلاعات",
        "گزارش‌گیری دقیق بازدیدها"
      ];

      // امکانات اضافه برای پلن‌های ۱ سال به بالا
      if (item.time >= 360) {
        baseFeatures.push("بهینه‌سازی پیشرفته سئو (SEO)");
        baseFeatures.push("دریافت تگ NFC یا تجهیزات رایگان (یا تخفیف بالای ۹۰٪)");
        baseFeatures.push("پشتیبانی ویژه و اولویت‌دار VIP");
      }

      return baseFeatures;
    },
  formatPrice(amount) {
      if (!amount) return { num: "رایگان", text: "" };

      const toman = Math.round(amount / 10);

      if (toman >= 1000000) {
        const millions = Math.floor(toman / 1000000);
        const thousands = Math.round((toman % 1000000) / 1000);

        if (thousands > 0) {
          return {
            num: `${this.persian(millions)} میلیون و ${this.persian(thousands)} هزار`,
            text: "تومان"
          };
        }
        return {
          num: `${this.persian(millions)} میلیون`,
          text: "تومان"
        };
      } else if (toman >= 1000) {
        const thousands = Math.round(toman / 1000);
        return {
          num: `${this.persian(thousands)} هزار`,
          text: "تومان"
        };
      }

      return {
        num: this.persian(toman.toLocaleString()),
        text: "تومان"
      };
    },

    getCardStyle(item) {
      const index = this.prices.findIndex((p) => p.id === item.id);
      const totalLength = this.prices.length;
      const reverseIndex = totalLength - 1 - index;

      switch (reverseIndex) {
        case 0:
          return { tag: "باصرفه‌ترین", color: "#ffb300", icon: "mdi-crown" };
        case 1:
          return { tag: "پیشنهاد ویژه", color: "#1de9b6", icon: "mdi-diamond-stone" };
        case 2:
          return { tag: "محبوب", color: "#8c9eff", icon: "mdi-star" };
        case 3:
          return { tag: "اقتصادی", color: "#ff8a65", icon: "mdi-wallet-bifold" };
        case 4:
          return { tag: "پایه", color: "#4fc3f7", icon: "mdi-flag-variant" };
        default:
          return { tag: "عادی", color: "#90a4ae", icon: "mdi-calendar-month" };
      }
    },
    getPrices() {
      this.priceloading = true;
      const typeValue = this.qrDataObject.value || this.qrtype;

      this.$api
        .get("user/price", { params: { type: typeValue } })
        .then((response) => {
          this.prices =
            response.data.data.sort((a, b) => a.time - b.time) || [];
        })
        .catch(({ response }) => {
          if (
            response?.data?.errors &&
            Object.keys(response.data.errors).length > 0
          ) {
            this.$modal.error(
              this.$t("error"),
              Object.values(response.data.errors)[0][0]
            );
          } else {
            this.$modal.error(this.$t("error"), this.$t("no_tariff_exists"));
          }
        })
        .finally(() => (this.priceloading = false));
    },
    upgradeQr(priceId) {
      this.priceloading = true;
      this.$api
        .put(`user/price/upgrade/${this.id_qr}/${priceId}`, {
          discount_code: !!this.discountField.model
            ? this.discountField.model
            : undefined,
        })
        .then(() => {
          this.discountField.show = false;
          this.priceloading = false;
          this.$modal.success(
            this.$t("success"),
            this.$t("qr_upgraded_successfully")
          );
          if (typeof this.debouncedFetch === "function") {
            this.debouncedFetch();
          }
        })
        .catch(({ response }) => {
          this.priceloading = false;
          if (response?.status === 402)
            this.$modal.error(
              this.$t("error"),
              this.$t("check_account_balance_and_try_again")
            );
          else if (response?.status === 422)
            this.$modal.error(
              "خطا",
              response?.data?.message ||
                response?.message ||
                response?.error?.message
            );
          else {
            this.$modal.error(
              this.$t("error"),
              this.$t("problem_with_qr_renewal_request")
            );
          }
        });
    },
    computedDiscount(item) {
      if (!item.discount) return item.price;
      const discountAmount = (item.price * item.discount) / 100;
      return item.price - discountAmount;
    },
  },
};
</script>
